import Modal from './Modal';
import { APP_VERSION } from '../version';
import { useTranslation } from '../i18n';
import { useAuth } from '../hooks/useAuth';
import './AnnouncementModal.scss';

/**
 * AnnouncementModal - Displays welcome, changelog, or info modals
 * 
 * All modes show intro + storage warning at the top.
 * Modes:
 * - 'welcome': First-time user - intro + warning only
 * - 'welcome-choice': First-time anonymous user - choice between login or local
 * - 'changelog': Returning user - intro + warning + new version highlights
 * - 'info': Manual access - intro + warning + full changelog
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether modal is visible
 * @param {'welcome'|'welcome-choice'|'changelog'|'info'} props.mode - Modal mode
 * @param {Array} [props.versions] - Array of changelog entries to display (for changelog/info modes)
 * @param {Function} props.onDismiss - Callback when modal is dismissed
 * @param {Function} [props.onLogin] - Callback for login button (welcome-choice mode)
 * @param {Function} [props.onUseLocally] - Callback for use locally button (welcome-choice mode)
 */
function AnnouncementModal({ isOpen, mode, versions = [], onDismiss, onLogin, onUseLocally }) {
  const { t, language } = useTranslation();
  const { isAuthenticated } = useAuth();
  
  const getTitle = () => {
    switch (mode) {
      case 'welcome':
        return t('announcement.welcomeTitle');
      case 'welcome-choice':
        return t('announcement.chooseHowToUse');
      case 'changelog':
        return t('announcement.changelogTitle');
      case 'info':
        return t('announcement.aboutTitle');
      default:
        return t('app.title');
    }
  };

  const getButtonLabel = () => {
    switch (mode) {
      case 'welcome':
        return t('announcement.getStarted');
      case 'changelog':
        return t('announcement.gotIt');
      case 'info':
        return t('common.close');
      default:
        return t('common.close');
    }
  };

  const renderIntroAndWarning = () => (
    <>
      <p className="announcement-modal__intro">
        {t('announcement.welcomeIntro')}
      </p>
      
      <div className={`announcement-modal__warning ${isAuthenticated ? 'announcement-modal__warning--info' : ''}`}>
        {isAuthenticated ? (
          <>
            <strong>{t('announcement.note')}</strong> {t('announcement.storageServer')}
          </>
        ) : (
          <>
            <strong>{t('announcement.important')}</strong> {t('announcement.storageWarning')}
          </>
        )}
      </div>
    </>
  );

  const renderWelcomeChoice = () => (
    <>
      <p className="announcement-modal__intro">
        {t('announcement.welcomeIntro')}
      </p>
      
      <div className="announcement-modal__choices">
        <button
          className="announcement-modal__choice announcement-modal__choice--primary"
          onClick={onLogin}
        >
          <span className="announcement-modal__choice-icon">☁</span>
          <div className="announcement-modal__choice-content">
            <span className="announcement-modal__choice-title">
              {t('announcement.loginOptionRecommended')}
            </span>
            <span className="announcement-modal__choice-desc">
              {t('announcement.loginOptionDesc')}
            </span>
          </div>
        </button>
        
        <button
          className="announcement-modal__choice"
          onClick={onUseLocally}
        >
          <span className="announcement-modal__choice-icon">💾</span>
          <div className="announcement-modal__choice-content">
            <span className="announcement-modal__choice-title">
              {t('announcement.localOption')}
            </span>
            <span className="announcement-modal__choice-desc">
              {t('announcement.localOptionDesc')}
            </span>
          </div>
        </button>
      </div>
      
      <div className="announcement-modal__warning">
        <strong>{t('announcement.note')}</strong> {t('announcement.localWarningShort')}
      </div>
    </>
  );

  // Get translated changelog entry
  const getTranslatedVersion = (version) => {
    // Try to get translated title and highlights from the version object
    // Version objects can have { title: { en, de }, highlights: { en, de } }
    // or just plain strings if not yet translated
    let title = version.title;
    let highlights = version.highlights;
    
    if (typeof version.title === 'object' && version.title[language]) {
      title = version.title[language];
    } else if (typeof version.title === 'object' && version.title.en) {
      title = version.title.en;
    }
    
    if (typeof version.highlights === 'object' && version.highlights[language]) {
      highlights = version.highlights[language];
    } else if (typeof version.highlights === 'object' && version.highlights.en) {
      highlights = version.highlights.en;
    }
    
    return { title, highlights };
  };

  const renderChangelog = () => {
    if (!versions || versions.length === 0) return null;
    
    return (
      <>
        <div className="announcement-modal__divider" />
        <h4 className="announcement-modal__section-title">
          {mode === 'changelog' ? t('announcement.whatsNew') : t('announcement.recentChanges')}
        </h4>
        <div className="announcement-modal__changelog">
          {versions.map((version) => {
            const { title, highlights } = getTranslatedVersion(version);
            return (
              <div key={version.version} className="announcement-modal__version">
                <div className="announcement-modal__version-header">
                  <span className="announcement-modal__version-number">v{version.version}</span>
                  <span className="announcement-modal__version-date">{version.date}</span>
                </div>
                <h4 className="announcement-modal__version-title">{title}</h4>
                <ul className="announcement-modal__highlights">
                  {highlights.map((highlight, index) => (
                    <li key={index}>{highlight}</li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </>
    );
  };

  const renderContent = () => {
    if (mode === 'welcome-choice') {
      return renderWelcomeChoice();
    }
    
    if (mode === 'info') {
      // Info mode: show version badge at top
      return (
        <>
          <div className="announcement-modal__info-header">
            <span className="announcement-modal__version-badge">{t('announcement.version', { version: APP_VERSION })}</span>
          </div>
          {renderIntroAndWarning()}
          {renderChangelog()}
        </>
      );
    }
    
    // Welcome and changelog modes: intro + warning + optional changelog
    return (
      <>
        {renderIntroAndWarning()}
        {mode === 'changelog' && renderChangelog()}
      </>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onDismiss}
      title={getTitle()}
      maxWidth={560}
      showCloseButton={false}
    >
      <div className="announcement-modal">
        {renderContent()}
        
        {mode !== 'welcome-choice' && (
          <div className="announcement-modal__actions">
            <button
              className="announcement-modal__btn"
              onClick={onDismiss}
            >
              {getButtonLabel()}
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
}

export default AnnouncementModal;
